import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Employee_Api_EndPoints } from "../global/end-points";
import * as userService from "./userService";

/**
 * useActivityLogger
 * - Prefixes activityDescription as `${moduleCode}-${description || activity}`
 * - Reads UserId / OnBehalfUserId from sessionStorage (with common key variants)
 * - Fills route from react-router location
 * - Sends occuredDateTime (note the API's spelling)
 */
export default function useActivityLogger({
  moduleCode = "DPY",
  endpoint,
  silent = true,
} = {}) {
  const location = useLocation();
  const url =
    endpoint ||
    Employee_Api_EndPoints?.INSERT_ACTIVITY_LOG ||
    "/api/ActivityLog/InsertActivityLog";

  const toInt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const logActivity = useCallback(
    async (meta = {}) => {
      try {
        const payload = {
          userId: toInt(
            meta.userId ??
            sessionStorage.UserId ??
            sessionStorage.getItem("UserId")
          ),
          onBehalfUserId: toInt(
            meta.onBehalfUserId ??
            sessionStorage.OnBehalfUserId ??
            sessionStorage.getItem("OnBehalfUserId") ??
            sessionStorage.onBehalfUserId ??
            sessionStorage.getItem("onBehalfUserId")
          ),
          activity: meta.activity, // e.g., "BUTTON_CLICK", "API_CALL", "LOGIN"
          activityDescription: `${moduleCode}-${meta.description ?? meta.activity ?? ""}`,
          component: meta.component ?? "", // e.g., "Dashboard.InstantPayout.Button"
          route:
            meta.route ?? `${location.pathname}${location.search || ""}`,
          occuredDateTime: (
            meta.occurredAt ??
            new Date().toLocaleString("sv-SE", { timeZone: "America/New_York" })
          ).replace(" ", "T"), // ensures ISO-like format
        };

        console.log({ payload });

        const resp = await fetch(url, {
          method: "POST",
          headers: {
            accept: "*/*",
            "Content-Type": "application/json",
            AccessToken: userService?.GetUserToken?.() ?? "",
          },
          body: JSON.stringify(payload),
          keepalive: true, // helps when navigating away
        });

        if (!resp.ok && !silent) {
          const txt = await resp.text().catch(() => "");
          // eslint-disable-next-line no-console
          console.warn("ActivityLog failed", resp.status, txt, payload);
        }
        return resp.ok;
      } catch (err) {
        if (!silent) {
          // eslint-disable-next-line no-console
          console.warn("ActivityLog error", err);
        }
        return false;
      }
    },
    [location.pathname, location.search, moduleCode, silent, url]
  );

  // Wrap an existing handler so it logs (fire-and-forget) before running your handler
  const wrap = useCallback(
    (handler, meta) =>
      (e) => {
        logActivity(meta);
        if (typeof handler === "function") return handler(e);
      },
    [logActivity]
  );

  // Convenience helpers
  const logClick = useCallback(
    (component, description, extra = {}) =>
      wrap(extra.onClick, {
        activity: "BUTTON_CLICK",
        description,
        component,
        ...extra,
      }),
    [wrap]
  );

  const logApiCall = useCallback(
    (component, description, extra = {}) =>
      logActivity({
        activity: "API_CALL",
        description,
        component,
        ...extra,
      }),
    [logActivity]
  );

  return { logActivity, wrap, logClick, logApiCall };
}
